<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { emitter } from 'shared/helpers/mitt';
import FunnelAPI from '../../../../api/funnel';
import agents from '../../../../api/agents';
import Button from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

const emit = defineEmits(['switchView']);

const { t } = useI18n();
const route = useRoute();
const store = useStore();

const agentsList = ref([]);
const saving = ref(false);
const availableItemCustomAttributeKeys = ref([]);
const stageMessageTemplates = ref([]);
const funnelsList = ref([]);

const selectedFunnel = computed(
  () => store.getters['funnel/getSelectedFunnel']
);
const inboxesList = computed(() => store.getters['inboxes/getInboxes'] || []);

const stageId = computed(() => route.query.stage);
const conversationCustomAttributes = computed(
  () =>
    store.getters['attributes/getAttributesByModel']?.(
      'conversation_attribute'
    ) || []
);
const contactCustomAttributes = computed(
  () =>
    store.getters['attributes/getAttributesByModel']?.('contact_attribute') ||
    []
);
const conversationCustomAttributeOptions = computed(() =>
  conversationCustomAttributes.value
    .map(attr => ({
      key: attr?.attribute_key,
      name: attr?.attribute_display_name || attr?.attribute_key,
    }))
    .filter(attr => attr.key)
);
const contactCustomAttributeOptions = computed(() =>
  contactCustomAttributes.value
    .map(attr => ({
      key: attr?.attribute_key,
      name: attr?.attribute_display_name || attr?.attribute_key,
    }))
    .filter(attr => attr.key)
);

const stageMeta = computed(() => {
  const f = selectedFunnel.value;
  const sid = stageId.value;
  if (!f?.stages || !sid) return null;
  return f.stages[sid] || null;
});

const stageName = computed(() => stageMeta.value?.name || stageId.value || '');
const darkenColor = color => {
  const hex = color.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  const darkenAmount = 0.65;
  const dr = Math.floor(r * darkenAmount);
  const dg = Math.floor(g * darkenAmount);
  const db = Math.floor(b * darkenAmount);

  return `#${dr.toString(16).padStart(2, '0')}${dg
    .toString(16)
    .padStart(2, '0')}${db.toString(16).padStart(2, '0')}`;
};
const contrastTextOnSolidHex = hex => {
  if (!hex || typeof hex !== 'string') return '#0f172a';
  const raw = hex.replace('#', '').slice(0, 6);
  if (raw.length !== 6) return '#0f172a';
  const r = parseInt(raw.substring(0, 2), 16) / 255;
  const g = parseInt(raw.substring(2, 4), 16) / 255;
  const b = parseInt(raw.substring(4, 6), 16) / 255;
  if ([r, g, b].some(c => Number.isNaN(c))) return '#0f172a';
  const lin = c => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
  const luminance = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
  return luminance > 0.45 ? '#0f172a' : '#ffffff';
};
const stageHeaderStyle = computed(() => {
  const color = stageMeta.value?.color;
  return color ? { backgroundColor: color } : {};
});
const stageHeaderTextColor = computed(() => {
  const color = stageMeta.value?.color;
  return color ? contrastTextOnSolidHex(color) : null;
});
const stageBackButtonStyle = computed(() => {
  const textColor = stageHeaderTextColor.value;
  if (!textColor) return {};

  const isDarkText = textColor === '#0f172a';
  return {
    color: textColor,
    '--stage-back-btn-hover-bg': isDarkText
      ? 'rgba(255, 255, 255, 0.28)'
      : 'rgba(0, 0, 0, 0.18)',
  };
});
const stageSaveButtonStyle = computed(() => {
  const color = stageMeta.value?.color;
  if (!color) return {};
  const baseColor = darkenColor(color);
  const hoverColor = color;

  return {
    '--button-bg-color': baseColor,
    '--button-text-color': contrastTextOnSolidHex(baseColor),
    '--button-hover-text-color': contrastTextOnSolidHex(hoverColor),
    '--button-hover-bg-color': hoverColor,
  };
});

const otherStages = computed(() => {
  const f = selectedFunnel.value;
  if (!f?.stages) return [];
  return Object.entries(f.stages).map(([id, s]) => ({
    id,
    name: s?.name || id,
  }));
});
const funnelOptions = computed(() =>
  (funnelsList.value || []).map(f => ({
    id: String(f.id),
    name: f.name || String(f.id),
    isCurrent: String(f.id) === String(selectedFunnel.value?.id),
  }))
);
const targetStagesByFunnel = computed(() => {
  const map = {};
  (funnelsList.value || []).forEach(funnel => {
    const stages = funnel?.stages || {};
    map[String(funnel.id)] = Object.entries(stages).map(([id, stage]) => ({
      id,
      name: stage?.name || id,
    }));
  });
  return map;
});

const rules = ref([]);
const scheduledFollowUp = ref({
  enabled: false,
  sequence: [],
  business_days_only: false,
  max_attempts: 0,
  fallback_stage_key: '',
  cancel_on_status: ['won', 'lost'],
  inbox_id: null,
});

// Computed: inbox selecionada (pra detectar tipo de canal e mostrar templates)
const selectedFollowUpInbox = computed(() => {
  return inboxesList.value.find(
    i => Number(i.id) === Number(scheduledFollowUp.value.inbox_id)
  );
});

const isWhatsappOfficialInbox = computed(() => {
  return selectedFollowUpInbox.value?.channel_type === 'Channel::Whatsapp';
});

const followUpInboxOptions = computed(() => {
  return inboxesList.value
    .filter(
      i =>
        i.channel_type === 'Channel::Whatsapp' ||
        i.channel_type === 'Channel::Api'
    )
    .map(i => ({
      id: i.id,
      name: i.name,
      type:
        i.channel_type === 'Channel::Whatsapp'
          ? 'WhatsApp Oficial (Meta)'
          : i.channel_type === 'Channel::Api'
            ? 'API (WAHA / WhatsApp não oficial)'
            : i.channel_type,
      raw_type: i.channel_type,
    }));
});

// Templates do whatsapp da inbox selecionada (vem do Meta)
const whatsappTemplatesAvailable = computed(() => {
  if (!isWhatsappOfficialInbox.value) return [];
  const tpl = selectedFollowUpInbox.value?.message_templates;
  return Array.isArray(tpl)
    ? tpl.filter(t => t.status?.toLowerCase() === 'approved')
    : [];
});
const currentStageTemplates = computed(() => stageMessageTemplates.value);

// Conta as variaveis {{N}} do template selecionado e ajusta whatsapp_template_params
const getTemplateVariables = templateName => {
  if (!templateName) return [];
  const tpl = whatsappTemplatesAvailable.value.find(
    t => t.name === templateName
  );
  if (!tpl) return [];
  // Verifica components -> body -> text procurando {{1}}, {{2}}, etc
  const components = tpl.components || [];
  let text = '';
  components.forEach(c => {
    if (c.type === 'BODY' && c.text) text += c.text + ' ';
    if (c.type === 'HEADER' && c.text) text += c.text + ' ';
  });
  const matches = text.match(/\{\{\d+\}\}/g) || [];
  // Unique sort
  return [...new Set(matches)].sort();
};

const onWhatsappTemplateChange = step => {
  const tpl = whatsappTemplatesAvailable.value.find(
    t => t.name === step.whatsapp_template_name
  );
  if (tpl) {
    step.whatsapp_template_language = tpl.language;
    const vars = getTemplateVariables(step.whatsapp_template_name);
    // Ajusta o array de params pro tamanho certo
    if (!Array.isArray(step.whatsapp_template_params))
      step.whatsapp_template_params = [];
    while (step.whatsapp_template_params.length < vars.length) {
      step.whatsapp_template_params.push({ type: 'static', value: '' });
    }
    step.whatsapp_template_params = step.whatsapp_template_params.slice(
      0,
      vars.length
    );
  } else {
    step.whatsapp_template_language = null;
    step.whatsapp_template_params = [];
  }
};

// Lista de etapas disponiveis (exceto a etapa atual) pra escolher como fallback
const availableFallbackStages = computed(() => {
  const stages = selectedFunnel.value?.stages || {};
  return Object.keys(stages)
    .filter(k => k !== stageId.value)
    .map(k => ({ key: k, name: stages[k]?.name || k }));
});

const buildEmptyScheduledFollowUpStep = () => ({
  day: 1,
  time: '',
  template_id: '',
  content_override: '',
  attachment_url: '',
  attachment_type: 'image',
  whatsapp_template_name: null,
  whatsapp_template_language: null,
  whatsapp_template_params: [],
});

const normalizeScheduledFollowUpSequence = rawScheduledFollowUp => {
  const rawSequence = Array.isArray(rawScheduledFollowUp?.sequence)
    ? rawScheduledFollowUp.sequence
    : [];

  const normalizedSequence = rawSequence
    .map(step => ({
      day: Number(step?.day) > 0 ? Number(step.day) : 1,
      time:
        typeof step?.time === 'string' && /^\d{1,2}:\d{2}$/.test(step.time)
          ? step.time
          : '',
      template_id: step?.template_id ? String(step.template_id) : '',
      content_override: step?.content_override || '',
      attachment_url: step?.attachment_url || '',
      attachment_type: step?.attachment_type || 'image',
      whatsapp_template_name: step?.whatsapp_template_name || null,
      whatsapp_template_language: step?.whatsapp_template_language || null,
      whatsapp_template_params: Array.isArray(step?.whatsapp_template_params)
        ? step.whatsapp_template_params
        : [],
    }))
    .filter(
      step => step.template_id || step.content_override || step.attachment_url
    );

  if (normalizedSequence.length > 0) {
    return normalizedSequence;
  }

  const legacyDay =
    Number(rawScheduledFollowUp?.days_in_stage) > 0
      ? Number(rawScheduledFollowUp?.days_in_stage)
      : 1;
  const legacyTemplateId = rawScheduledFollowUp?.template_id
    ? String(rawScheduledFollowUp.template_id)
    : '';

  return legacyTemplateId
    ? [{ day: legacyDay, template_id: legacyTemplateId }]
    : [buildEmptyScheduledFollowUpStep()];
};

const stringOperators = [
  { id: 'equal_to', labelKey: 'OP_EQUAL_TO' },
  { id: 'contains', labelKey: 'OP_CONTAINS' },
  { id: 'not_equal_to', labelKey: 'OP_NOT_EQUAL_TO' },
];

const numericOperators = [
  { id: 'equal_to', labelKey: 'OP_EQUAL_TO' },
  { id: 'greater_than', labelKey: 'OP_GREATER_THAN' },
  { id: 'less_than', labelKey: 'OP_LESS_THAN' },
  { id: 'greater_than_or_equal', labelKey: 'OP_GREATER_THAN_OR_EQUAL' },
  { id: 'less_than_or_equal', labelKey: 'OP_LESS_THAN_OR_EQUAL' },
];

const dealStatusOptions = [
  { id: 'open', labelKey: 'STATUS_OPEN' },
  { id: 'won', labelKey: 'STATUS_WON' },
  { id: 'lost', labelKey: 'STATUS_LOST' },
];

const priorityOptions = [
  { id: 'none', labelKey: 'PRIORITY_NONE' },
  { id: 'low', labelKey: 'PRIORITY_LOW' },
  { id: 'medium', labelKey: 'PRIORITY_MEDIUM' },
  { id: 'high', labelKey: 'PRIORITY_HIGH' },
  { id: 'urgent', labelKey: 'PRIORITY_URGENT' },
];

const defaultStageAutomationActionConfig = action => {
  switch (action) {
    case 'assign_agent':
      return { agent_id: '' };
    case 'create_note':
      return { note_text: '' };
    case 'move_to_stage':
      return { stage: '' };
    case 'move_to_funnel':
      return { funnel_id: '', stage: '' };
    case 'set_item_status':
      return { status: 'open' };
    case 'set_item_priority':
      return { priority: 'none' };
    case 'set_item_value':
      return { value: '' };
    case 'set_item_custom_attribute':
      return { key: '', value: '', type: 'text' };
    case 'webhook':
      return { url: '' };
    case 'clear_assigned_agents':
    case 'complete_required_checklist':
    default:
      return {};
  }
};

const onConditionTypeChange = rule => {
  const type = rule.condition.type;
  if (type === 'item_status_equals') {
    rule.condition = { type, value: 'open' };
  } else if (type === 'item_priority_equals') {
    rule.condition = { type, value: 'none' };
  } else if (type === 'item_value_compare') {
    rule.condition = { type, operator: 'equal_to', value: '' };
  } else if (
    type === 'conversation_custom_attribute' ||
    type === 'contact_custom_attribute_equals'
  ) {
    rule.condition = { type, operator: 'equal_to', key: '', value: '' };
  } else if (type === 'item_custom_attribute_equals') {
    rule.condition = { type, key: '', value: '' };
  } else {
    rule.condition = { type, operator: 'equal_to', key: '', value: '' };
  }
};

const syncRulesFromFunnel = () => {
  const meta = stageMeta.value;
  const raw = meta?.stage_automations;
  rules.value = Array.isArray(raw) ? JSON.parse(JSON.stringify(raw)) : [];

  const rawScheduledFollowUp = meta?.scheduled_follow_up;
  scheduledFollowUp.value = {
    enabled: rawScheduledFollowUp?.enabled === true,
    sequence: normalizeScheduledFollowUpSequence(rawScheduledFollowUp),
    business_days_only: rawScheduledFollowUp?.business_days_only === true,
    max_attempts: Number(rawScheduledFollowUp?.max_attempts) || 0,
    fallback_stage_key: rawScheduledFollowUp?.fallback_stage_key || '',
    cancel_on_status: Array.isArray(rawScheduledFollowUp?.cancel_on_status)
      ? rawScheduledFollowUp.cancel_on_status
      : ['won', 'lost'],
    inbox_id: rawScheduledFollowUp?.inbox_id
      ? Number(rawScheduledFollowUp.inbox_id)
      : null,
  };
};

watch(
  () => [selectedFunnel.value?.id, selectedFunnel.value?.stages, stageId.value],
  () => syncRulesFromFunnel(),
  { immediate: true, deep: true }
);

const addStageAutomationRule = () => {
  rules.value.push({
    enabled: true,
    condition: { type: 'always', operator: 'equal_to', key: '', value: '' },
    action: 'assign_agent',
    action_config: { agent_id: '' },
  });
};

const removeStageAutomationRule = index => {
  rules.value.splice(index, 1);
};

const onStageAutomationActionChange = rule => {
  rule.action_config = defaultStageAutomationActionConfig(rule.action);
};

const opLabel = key => t(`KANBAN.STAGE_AUTOMATIONS.${key}`);

const fetchAgentsList = async () => {
  try {
    const { data } = await agents.get();
    agentsList.value = data || [];
  } catch {
    agentsList.value = [];
  }
};

const loadConversationAndContactAttributes = async () => {
  try {
    await store.dispatch('attributes/get');
  } catch {
    /* atributos opcionais para condição */
  }
};

const loadItemCustomAttributeKeysForFunnel = async funnelId => {
  if (!funnelId) {
    availableItemCustomAttributeKeys.value = [];
    return;
  }

  try {
    const response = await FunnelAPI.get();
    const funnels = response.data || [];
    const currentFunnel = funnels.find(f => String(f.id) === String(funnelId));
    const globalAttrs = Array.isArray(currentFunnel?.global_custom_attributes)
      ? currentFunnel.global_custom_attributes
      : [];

    availableItemCustomAttributeKeys.value = [
      ...new Set(globalAttrs.map(attr => attr?.name).filter(Boolean)),
    ];
  } catch {
    availableItemCustomAttributeKeys.value = [];
  }
};

const loadFunnelsList = async () => {
  try {
    const response = await FunnelAPI.get();
    funnelsList.value = Array.isArray(response?.data) ? response.data : [];
  } catch {
    funnelsList.value = [];
  }
};

const loadStageMessageTemplates = async () => {
  const funnelId = selectedFunnel.value?.id;
  const sid = stageId.value;
  if (!funnelId || !sid) {
    stageMessageTemplates.value = [];
    return;
  }

  try {
    const response = await FunnelAPI.get();
    const funnels = response.data || [];
    const currentFunnel = funnels.find(f => String(f.id) === String(funnelId));
    const templates = currentFunnel?.stages?.[sid]?.message_templates;
    stageMessageTemplates.value = Array.isArray(templates) ? templates : [];
  } catch {
    stageMessageTemplates.value = [];
  }
};

watch(
  () => selectedFunnel.value?.id,
  async funnelId => {
    await loadItemCustomAttributeKeysForFunnel(funnelId);
  },
  { immediate: true }
);

watch(
  () => [selectedFunnel.value?.id, stageId.value],
  () => {
    loadStageMessageTemplates();
  },
  { immediate: true }
);

onMounted(async () => {
  fetchAgentsList();
  loadConversationAndContactAttributes();
  loadFunnelsList();
  loadStageMessageTemplates();
  if (!inboxesList.value.length) {
    try {
      await store.dispatch('inboxes/get');
    } catch {
      /* inboxes opcional para condição */
    }
  }
});

const goBack = () => {
  emit('switchView', 'kanban');
};

const addScheduledFollowUpStep = () => {
  scheduledFollowUp.value.sequence.push(buildEmptyScheduledFollowUpStep());
};

const removeScheduledFollowUpStep = index => {
  scheduledFollowUp.value.sequence.splice(index, 1);
  if (!scheduledFollowUp.value.sequence.length) {
    scheduledFollowUp.value.sequence.push(buildEmptyScheduledFollowUpStep());
  }
};

const onMoveToFunnelTargetChange = rule => {
  if (!rule?.action_config) return;
  rule.action_config.stage = '';
};

const normalizedScheduledFollowUpStepsForSave = () => {
  return (scheduledFollowUp.value.sequence || [])
    .map(step => ({
      day: Number(step?.day) > 0 ? Number(step.day) : 1,
      time:
        typeof step?.time === 'string' && /^\d{1,2}:\d{2}$/.test(step.time)
          ? step.time
          : '',
      template_id: step?.template_id ? String(step.template_id) : '',
      // Antes o save jogava fora content_override/attachment/time/whatsapp_* e só
      // guardava {day, template_id} -> o follow-up de texto livre/anexo (WAHA) era
      // configurável mas nunca persistia nem disparava.
      content_override: step?.content_override || '',
      attachment_url: step?.attachment_url || '',
      attachment_type: step?.attachment_type || 'image',
      whatsapp_template_name: step?.whatsapp_template_name || null,
      whatsapp_template_language: step?.whatsapp_template_language || null,
      whatsapp_template_params: Array.isArray(step?.whatsapp_template_params)
        ? step.whatsapp_template_params
        : [],
    }))
    .filter(
      step => step.template_id || step.content_override || step.attachment_url
    )
    .sort((a, b) => a.day - b.day);
};

const save = async () => {
  const f = selectedFunnel.value;
  const sid = stageId.value;
  if (!f?.id || !sid || !stageMeta.value) {
    emitter.emit('newToastMessage', {
      message: t('KANBAN.STAGE_AUTOMATIONS.ERROR_NO_STAGE'),
      action: { type: 'error' },
    });
    return;
  }

  try {
    saving.value = true;
    const updatedStages = {
      ...f.stages,
      [sid]: {
        ...(f.stages[sid] || {}),
        stage_automations: rules.value,
        scheduled_follow_up: {
          enabled: scheduledFollowUp.value.enabled,
          sequence: normalizedScheduledFollowUpStepsForSave(),
          business_days_only:
            scheduledFollowUp.value.business_days_only === true,
          max_attempts: Number(scheduledFollowUp.value.max_attempts) || 0,
          fallback_stage_key: scheduledFollowUp.value.fallback_stage_key || '',
          cancel_on_status: scheduledFollowUp.value.cancel_on_status || [
            'won',
            'lost',
          ],
          inbox_id: scheduledFollowUp.value.inbox_id
            ? Number(scheduledFollowUp.value.inbox_id)
            : null,
        },
      },
    };
    await FunnelAPI.update(f.id, { stages: updatedStages });
    await store.dispatch('funnel/fetch');
    emitter.emit('newToastMessage', {
      message: t('KANBAN.STAGE_AUTOMATIONS.SAVED'),
      action: { type: 'success' },
    });
    goBack();
  } catch (e) {
    emitter.emit('newToastMessage', {
      message: t('KANBAN.STAGE_AUTOMATIONS.ERROR_SAVE'),
      action: { type: 'error' },
    });
  } finally {
    saving.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col h-full w-full min-h-0 bg-n-solid-1">
    <header
      class="flex flex-wrap items-center justify-between gap-3 px-4 py-3 shrink-0"
      :style="stageHeaderStyle"
    >
      <div class="flex items-center gap-3 min-w-0">
        <button
          type="button"
          class="stage-back-button p-2 rounded-xl shrink-0"
          :style="stageBackButtonStyle"
          :title="t('KANBAN.STAGE_AUTOMATIONS.BACK')"
          @click="goBack"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="m15 18-6-6 6-6" />
          </svg>
        </button>
        <div class="min-w-0">
          <div class="flex items-center gap-2 min-w-0">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
              class="shrink-0"
              :style="{ color: stageHeaderTextColor }"
            >
              <path
                d="M4 14a1 1 0 0 1-.78-1.63l9.9-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.9 10.2a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 11 14z"
              />
            </svg>
            <h1
              class="text-lg font-extrabold truncate"
              :style="{ color: stageHeaderTextColor }"
            >
              {{ t('KANBAN.STAGE_AUTOMATIONS.TITLE') }}
            </h1>
          </div>
          <p
            v-if="stageName"
            class="text-sm truncate"
            :style="{ color: stageHeaderTextColor, opacity: 0.85 }"
          >
            {{ stageName }}
          </p>
        </div>
      </div>
      <Button
        type="button"
        variant="solid"
        color="blue"
        size="sm"
        :is-loading="saving"
        :disabled="!stageMeta"
        :label="t('KANBAN.STAGE_AUTOMATIONS.SAVE')"
        :class="stageMeta?.color ? 'custom-colored-button' : ''"
        :style="stageSaveButtonStyle"
        @click="save"
      />
    </header>

    <div class="flex-1 overflow-y-auto p-4 md:p-6">
      <p v-if="!stageMeta" class="text-sm text-n-slate-11">
        {{ t('KANBAN.STAGE_AUTOMATIONS.ERROR_NO_STAGE') }}
      </p>

      <template v-else>
        <div
          class="mb-4 rounded-2xl px-4 py-3 border border-blue-200 bg-blue-50 text-blue-900 dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-100"
        >
          <p class="text-sm font-semibold">
            {{ t('KANBAN.STAGE_AUTOMATIONS.INFO_BANNER_TITLE') }}
          </p>
          <p class="mt-1 text-sm">
            {{ t('KANBAN.STAGE_AUTOMATIONS.INFO_BANNER_DESCRIPTION') }}
          </p>
          <p class="mt-2 text-xs text-blue-700 dark:text-blue-300">
            {{ t('KANBAN.STAGE_AUTOMATIONS.INFO_BANNER_NOTE') }}
          </p>
        </div>

        <p class="text-sm text-n-slate-11 mb-4 w-full">
          {{ t('KANBAN.STAGE_AUTOMATIONS.HELP') }}
        </p>

        <div
          class="rounded-2xl border border-n-weak p-4 mb-4 space-y-3 bg-n-solid-1"
        >
          <div class="flex items-center justify-between gap-3">
            <div>
              <p class="text-sm font-medium text-n-slate-12">
                {{ t('KANBAN.STAGE_AUTOMATIONS.SCHEDULED_FOLLOW_UP_TITLE') }}
              </p>
              <p class="text-xs text-n-slate-11">
                {{ t('KANBAN.STAGE_AUTOMATIONS.SCHEDULED_FOLLOW_UP_HELP') }}
              </p>
            </div>
            <Switch v-model="scheduledFollowUp.enabled" size="small" />
          </div>

          <div v-if="scheduledFollowUp.enabled" class="space-y-3">
            <p class="text-xs text-n-slate-11">
              {{
                t('KANBAN.STAGE_AUTOMATIONS.SCHEDULED_FOLLOW_UP_SEQUENCE_HELP')
              }}
            </p>

            <div
              v-for="(step, stepIndex) in scheduledFollowUp.sequence"
              :key="`scheduled-follow-up-step-${stepIndex}`"
              class="grid grid-cols-1 sm:grid-cols-[100px_100px_1fr_auto] gap-2"
            >
              <div>
                <label class="block text-xs font-medium text-n-slate-11 mb-1">
                  {{ t('KANBAN.STAGE_AUTOMATIONS.SCHEDULED_FOLLOW_UP_DAYS') }}
                </label>
                <input
                  v-model.number="step.day"
                  type="number"
                  min="1"
                  step="1"
                  class="w-full px-3 py-2 border border-n-weak rounded-xl text-sm bg-n-solid-1"
                  :placeholder="
                    t(
                      'KANBAN.STAGE_AUTOMATIONS.SCHEDULED_FOLLOW_UP_DAYS_PLACEHOLDER'
                    )
                  "
                />
              </div>

              <div>
                <label class="block text-xs font-medium text-n-slate-11 mb-1">
                  Hora
                </label>
                <input
                  v-model="step.time"
                  type="time"
                  class="w-full px-3 py-2 border border-n-weak rounded-xl text-sm bg-n-solid-1"
                  placeholder="--:--"
                />
              </div>

              <div class="space-y-2">
                <!-- Modo WhatsApp Oficial: dropdown de templates aprovados Meta -->
                <template v-if="isWhatsappOfficialInbox">
                  <label class="block text-xs font-medium text-n-slate-11 mb-1">
                    Template WhatsApp aprovado (Meta)
                  </label>
                  <select
                    v-model="step.whatsapp_template_name"
                    class="w-full px-3 py-2 border border-blue-300 dark:border-blue-700 rounded-xl text-sm bg-blue-50 dark:bg-blue-900/20"
                    @change="onWhatsappTemplateChange(step)"
                  >
                    <option :value="null">Selecione o template...</option>
                    <option
                      v-for="tpl in whatsappTemplatesAvailable"
                      :key="tpl.id || tpl.name + '-' + tpl.language"
                      :value="tpl.name"
                    >
                      {{ tpl.name }} ({{ tpl.language }})
                    </option>
                  </select>

                  <!-- Variaveis do template: pra cada {{N}} mostra um input -->
                  <div
                    v-if="
                      getTemplateVariables(step.whatsapp_template_name).length >
                      0
                    "
                    class="mt-2 space-y-2 p-2 bg-blue-50/50 dark:bg-blue-900/10 rounded"
                  >
                    <p
                      class="text-[11px] font-semibold text-blue-700 dark:text-blue-300"
                    >
                      Variaveis do template ({{
                        getTemplateVariables(step.whatsapp_template_name)
                          .length
                      }}):
                    </p>
                    <div
                      v-for="(
                        varParam, varIdx
                      ) in step.whatsapp_template_params"
                      :key="varIdx"
                      class="grid grid-cols-[100px_120px_1fr] gap-2 items-center"
                    >
                      <span class="text-xs text-n-slate-11">Variavel {{ varIdx + 1 }}:</span>
                      <select
                        v-model="varParam.type"
                        class="px-2 py-1 text-xs border border-n-weak rounded bg-n-solid-1"
                      >
                        <option value="static">Texto fixo</option>
                        <option value="contact_attribute">Do contato</option>
                        <option value="item_attribute">Do item kanban</option>
                      </select>
                      <select
                        v-if="varParam.type === 'contact_attribute'"
                        v-model="varParam.value"
                        class="px-2 py-1 text-xs border border-n-weak rounded bg-n-solid-1"
                      >
                        <option value="name">Nome</option>
                        <option value="email">Email</option>
                        <option value="phone_number">Telefone</option>
                      </select>
                      <select
                        v-else-if="varParam.type === 'item_attribute'"
                        v-model="varParam.value"
                        class="px-2 py-1 text-xs border border-n-weak rounded bg-n-solid-1"
                      >
                        <option value="title">Titulo do item</option>
                        <option value="description">Descricao</option>
                        <option value="value">Valor</option>
                        <option value="scheduled_at">
                          Agendamento (data/hora)
                        </option>
                      </select>
                      <input
                        v-else
                        v-model="varParam.value"
                        type="text"
                        placeholder="Texto fixo..."
                        class="px-2 py-1 text-xs border border-n-weak rounded bg-n-solid-1"
                      />
                    </div>
                  </div>
                </template>

                <!-- Modo nao-oficial (WAHA / Channel::Api): permitir modelo OU texto livre OU anexo -->
                <template v-else>
                  <label class="block text-xs font-medium text-n-slate-11 mb-1">
                    {{
                      t('KANBAN.STAGE_AUTOMATIONS.SCHEDULED_FOLLOW_UP_TEMPLATE')
                    }}
                    (opcional - reusa modelos do funil)
                  </label>
                  <select
                    v-model="step.template_id"
                    class="w-full px-3 py-2 border border-n-weak rounded-xl text-sm bg-n-solid-1"
                  >
                    <option value="">
                      Sem modelo / usar texto livre abaixo
                    </option>
                    <option
                      v-for="template in currentStageTemplates"
                      :key="template.id"
                      :value="String(template.id)"
                    >
                      {{ template.title || template.id }}
                    </option>
                  </select>

                  <label
                    class="block text-xs font-medium text-n-slate-11 mt-2 mb-1"
                  >
                    Texto livre (sobrepoe o modelo se preenchido)
                  </label>
                  <textarea
                    v-model="step.content_override"
                    rows="2"
                    placeholder="Ola {{contact.name}}, tudo bem? ..."
                    class="w-full px-3 py-2 border border-n-weak rounded-xl text-sm bg-n-solid-1 resize-y"
                  />

                  <div class="grid grid-cols-[1fr_120px] gap-2 mt-2">
                    <div>
                      <label
                        class="block text-xs font-medium text-n-slate-11 mb-1"
                      >
                        Anexo (URL imagem/video opcional)
                      </label>
                      <input
                        v-model="step.attachment_url"
                        type="url"
                        placeholder="https://..."
                        class="w-full px-3 py-2 border border-n-weak rounded-xl text-sm bg-n-solid-1"
                      />
                    </div>
                    <div>
                      <label
                        class="block text-xs font-medium text-n-slate-11 mb-1"
                      >
                        Tipo
                      </label>
                      <select
                        v-model="step.attachment_type"
                        :disabled="!step.attachment_url"
                        class="w-full px-3 py-2 border border-n-weak rounded-xl text-sm bg-n-solid-1 disabled:opacity-50"
                      >
                        <option value="image">Imagem</option>
                        <option value="video">Video</option>
                        <option value="file">Documento</option>
                        <option value="audio">Audio</option>
                      </select>
                    </div>
                  </div>
                </template>
              </div>

              <div class="sm:pt-6">
                <button
                  type="button"
                  class="h-10 w-10 inline-flex items-center justify-center rounded-xl border border-n-weak text-ruby-600 hover:text-ruby-700 dark:text-ruby-400"
                  :title="t('KANBAN.STAGE_AUTOMATIONS.REMOVE')"
                  :aria-label="t('KANBAN.STAGE_AUTOMATIONS.REMOVE')"
                  @click="removeScheduledFollowUpStep(stepIndex)"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M18 6 6 18" />
                    <path d="m6 6 12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div class="flex justify-start">
              <Button
                type="button"
                color="blue"
                size="sm"
                :label="
                  t('KANBAN.STAGE_AUTOMATIONS.SCHEDULED_FOLLOW_UP_ADD_STEP')
                "
                @click="addScheduledFollowUpStep"
              />
            </div>

            <!-- Configuracoes avancadas do follow-up (business days, max attempts, fallback) -->
            <div class="mt-5 pt-5 border-t border-n-weak space-y-4">
              <h4 class="text-sm font-medium text-n-slate-12">
                Configuracoes avancadas
              </h4>

              <!-- Inbox selector: define qual canal envia (WhatsApp Oficial vs WAHA/API) -->
              <div>
                <label class="block text-xs font-medium text-n-slate-11 mb-1">
                  Canal de envio (inbox)
                </label>
                <select
                  v-model="scheduledFollowUp.inbox_id"
                  class="w-full px-3 py-2 border border-n-weak rounded-xl text-sm bg-n-solid-1"
                >
                  <option :value="null">
                    Usar a inbox da conversa do lead (padrao)
                  </option>
                  <option
                    v-for="opt in followUpInboxOptions"
                    :key="opt.id"
                    :value="opt.id"
                  >
                    {{ opt.name }} - {{ opt.type }}
                  </option>
                </select>
                <p
                  v-if="isWhatsappOfficialInbox"
                  class="mt-1 text-[11px] text-blue-700 dark:text-blue-300"
                >
                  WhatsApp Oficial requer template aprovado pelo Meta. Selecione
                  o template em cada step abaixo.
                </p>
                <p
                  v-else-if="
                    selectedFollowUpInbox?.channel_type === 'Channel::Api'
                  "
                  class="mt-1 text-[11px] text-emerald-700 dark:text-emerald-300"
                >
                  WAHA / API: envia o conteudo do template como texto livre.
                </p>
              </div>

              <label class="flex items-center gap-2 cursor-pointer">
                <input
                  v-model="scheduledFollowUp.business_days_only"
                  type="checkbox"
                  class="rounded border-n-weak"
                />
                <span class="text-sm text-n-slate-12">
                  Enviar apenas em dias uteis (segunda a sexta)
                </span>
              </label>

              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label class="block text-xs font-medium text-n-slate-11 mb-1">
                    Limite de tentativas (0 = sem limite)
                  </label>
                  <input
                    v-model.number="scheduledFollowUp.max_attempts"
                    type="number"
                    min="0"
                    step="1"
                    class="w-full px-3 py-2 border border-n-weak rounded-xl text-sm bg-n-solid-1"
                    placeholder="0"
                  />
                  <p class="mt-1 text-[11px] text-n-slate-11">
                    Apos N follow-ups sem resposta, move pra etapa abaixo.
                  </p>
                </div>

                <div>
                  <label class="block text-xs font-medium text-n-slate-11 mb-1">
                    Mover automaticamente pra etapa
                  </label>
                  <select
                    v-model="scheduledFollowUp.fallback_stage_key"
                    class="w-full px-3 py-2 border border-n-weak rounded-xl text-sm bg-n-solid-1"
                    :disabled="Number(scheduledFollowUp.max_attempts) === 0"
                  >
                    <option value="">Nenhuma (apenas para o envio)</option>
                    <option
                      v-for="opt in availableFallbackStages"
                      :key="opt.key"
                      :value="opt.key"
                    >
                      {{ opt.name }}
                    </option>
                  </select>
                </div>
              </div>

              <div
                class="rounded-xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 px-3 py-2 text-xs text-emerald-800 dark:text-emerald-200"
              >
                <strong>Cancelamento automatico:</strong> a sequência é
                cancelada quando o item:
                <ul class="list-disc ml-4 mt-1">
                  <li>Sai desta etapa (qualquer motivo)</li>
                  <li>Vira Ganho ou Perdido</li>
                  <li>O cliente responde apos o ultimo follow-up enviado</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div class="flex items-center justify-between mb-3 w-full">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('KANBAN.STAGE_AUTOMATIONS.RULES') }}
          </span>
          <Button
            type="button"
            color="blue"
            size="sm"
            :label="t('KANBAN.STAGE_AUTOMATIONS.ADD_RULE')"
            @click="addStageAutomationRule"
          />
        </div>

        <div
          v-if="rules.length === 0"
          class="empty-rules-placeholder w-full rounded-2xl border border-dashed border-n-weak px-6 py-10 flex flex-col items-center text-center bg-n-alpha-1"
        >
          <div class="empty-rules-placeholder__icon-wrapper mb-4">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 48 48"
              fill="none"
              aria-hidden="true"
              class="empty-rules-placeholder__icon"
            >
              <rect
                x="8"
                y="10"
                width="32"
                height="28"
                rx="8"
                class="empty-rules-placeholder__shape"
              />
              <path
                d="M16 19h16M16 25h10M24 31h8"
                class="empty-rules-placeholder__line"
              />
              <circle
                cx="15"
                cy="31"
                r="3.5"
                class="empty-rules-placeholder__plus-bg"
              />
              <path
                d="M15 29.7v2.6M13.7 31h2.6"
                class="empty-rules-placeholder__plus"
              />
            </svg>
          </div>
          <p class="text-sm font-semibold text-n-slate-12 mb-1">
            {{ t('KANBAN.STAGE_AUTOMATIONS.EMPTY') }}
          </p>
          <p class="text-sm text-n-slate-11 max-w-xl mb-4">
            {{ t('KANBAN.STAGE_AUTOMATIONS.INFO_BANNER_DESCRIPTION') }}
          </p>
          <Button
            type="button"
            color="blue"
            size="sm"
            :label="t('KANBAN.STAGE_AUTOMATIONS.ADD_RULE')"
            @click="addStageAutomationRule"
          />
        </div>

        <div v-else class="space-y-3 w-full">
          <div
            v-for="(rule, ruleIndex) in rules"
            :key="ruleIndex"
            class="p-4 rounded-2xl border border-n-weak space-y-3 bg-n-alpha-1"
          >
            <div class="flex items-center justify-between gap-2">
              <Switch v-model="rule.enabled" size="small" />
              <button
                type="button"
                class="text-sm text-ruby-600 hover:text-ruby-700 dark:text-ruby-400"
                @click="removeStageAutomationRule(ruleIndex)"
              >
                {{ t('KANBAN.STAGE_AUTOMATIONS.REMOVE') }}
              </button>
            </div>

            <div class="grid grid-cols-1 gap-3">
              <div>
                <label class="block text-xs font-medium text-n-slate-11 mb-1">
                  {{ t('KANBAN.STAGE_AUTOMATIONS.CONDITION_TYPE') }}
                </label>
                <select
                  v-model="rule.condition.type"
                  class="w-full px-3 py-2 border border-n-weak rounded-xl text-sm bg-n-solid-1"
                  @change="onConditionTypeChange(rule)"
                >
                  <option value="always">
                    {{ t('KANBAN.STAGE_AUTOMATIONS.CONDITION_ALWAYS') }}
                  </option>
                  <option value="item_custom_attribute_equals">
                    {{ t('KANBAN.STAGE_AUTOMATIONS.CONDITION_ITEM_ATTR') }}
                  </option>
                  <option value="no_agent_assigned">
                    {{ t('KANBAN.STAGE_AUTOMATIONS.CONDITION_NO_AGENT') }}
                  </option>
                  <option value="has_conversation">
                    {{
                      t('KANBAN.STAGE_AUTOMATIONS.CONDITION_HAS_CONVERSATION')
                    }}
                  </option>
                  <option value="item_status_equals">
                    {{ t('KANBAN.STAGE_AUTOMATIONS.CONDITION_ITEM_STATUS') }}
                  </option>
                  <option value="item_priority_equals">
                    {{ t('KANBAN.STAGE_AUTOMATIONS.CONDITION_ITEM_PRIORITY') }}
                  </option>
                  <option value="item_value_compare">
                    {{ t('KANBAN.STAGE_AUTOMATIONS.CONDITION_ITEM_VALUE') }}
                  </option>
                  <option value="required_checklist_complete">
                    {{
                      t('KANBAN.STAGE_AUTOMATIONS.CONDITION_CHECKLIST_REQUIRED')
                    }}
                  </option>
                  <option value="contact_has_tag">
                    {{ t('KANBAN.STAGE_AUTOMATIONS.CONDITION_CONTACT_TAG') }}
                  </option>
                  <option value="conversation_inbox_equals">
                    {{
                      t('KANBAN.STAGE_AUTOMATIONS.CONDITION_CONVERSATION_INBOX')
                    }}
                  </option>
                  <option value="conversation_custom_attribute">
                    {{
                      t('KANBAN.STAGE_AUTOMATIONS.CONDITION_CONVERSATION_ATTR')
                    }}
                  </option>
                  <option value="contact_custom_attribute_equals">
                    {{ t('KANBAN.STAGE_AUTOMATIONS.CONDITION_CONTACT_ATTR') }}
                  </option>
                </select>
              </div>

              <template
                v-if="rule.condition.type === 'item_custom_attribute_equals'"
              >
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <select
                    v-model="rule.condition.key"
                    class="w-full px-3 py-2 border border-n-weak rounded-xl text-sm bg-n-solid-1"
                  >
                    <option value="">
                      {{ t('KANBAN.STAGE_AUTOMATIONS.ATTR_NAME_PLACEHOLDER') }}
                    </option>
                    <option
                      v-for="attrName in availableItemCustomAttributeKeys"
                      :key="attrName"
                      :value="attrName"
                    >
                      {{ attrName }}
                    </option>
                  </select>
                  <input
                    v-model="rule.condition.value"
                    type="text"
                    class="w-full px-3 py-2 border border-n-weak rounded-xl text-sm bg-n-solid-1"
                    :placeholder="
                      t('KANBAN.STAGE_AUTOMATIONS.ATTR_VALUE_PLACEHOLDER')
                    "
                  />
                </div>
              </template>

              <template v-if="rule.condition.type === 'item_status_equals'">
                <select
                  v-model="rule.condition.value"
                  class="w-full px-3 py-2 border border-n-weak rounded-xl text-sm bg-n-solid-1"
                >
                  <option
                    v-for="opt in dealStatusOptions"
                    :key="opt.id"
                    :value="opt.id"
                  >
                    {{ t(`KANBAN.STAGE_AUTOMATIONS.${opt.labelKey}`) }}
                  </option>
                </select>
              </template>

              <template v-if="rule.condition.type === 'item_priority_equals'">
                <select
                  v-model="rule.condition.value"
                  class="w-full px-3 py-2 border border-n-weak rounded-xl text-sm bg-n-solid-1"
                >
                  <option
                    v-for="opt in priorityOptions"
                    :key="opt.id"
                    :value="opt.id"
                  >
                    {{ t(`KANBAN.STAGE_AUTOMATIONS.${opt.labelKey}`) }}
                  </option>
                </select>
              </template>

              <template v-if="rule.condition.type === 'item_value_compare'">
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <select
                    v-model="rule.condition.operator"
                    class="w-full px-3 py-2 border border-n-weak rounded-xl text-sm bg-n-solid-1"
                  >
                    <option
                      v-for="op in numericOperators"
                      :key="op.id"
                      :value="op.id"
                    >
                      {{ opLabel(op.labelKey) }}
                    </option>
                  </select>
                  <input
                    v-model="rule.condition.value"
                    type="number"
                    step="any"
                    class="w-full px-3 py-2 border border-n-weak rounded-xl text-sm bg-n-solid-1"
                    :placeholder="
                      t('KANBAN.STAGE_AUTOMATIONS.VALUE_PLACEHOLDER')
                    "
                  />
                </div>
              </template>

              <template v-if="rule.condition.type === 'contact_has_tag'">
                <input
                  v-model="rule.condition.value"
                  type="text"
                  class="w-full px-3 py-2 border border-n-weak rounded-xl text-sm bg-n-solid-1"
                  :placeholder="t('KANBAN.STAGE_AUTOMATIONS.TAG_PLACEHOLDER')"
                />
              </template>

              <template
                v-if="rule.condition.type === 'conversation_inbox_equals'"
              >
                <select
                  v-model="rule.condition.value"
                  class="w-full px-3 py-2 border border-n-weak rounded-xl text-sm bg-n-solid-1"
                >
                  <option value="">
                    {{ t('KANBAN.STAGE_AUTOMATIONS.SELECT_INBOX') }}
                  </option>
                  <option
                    v-for="inbox in inboxesList"
                    :key="inbox.id"
                    :value="String(inbox.id)"
                  >
                    {{ inbox.name }}
                  </option>
                </select>
              </template>

              <template
                v-if="
                  rule.condition.type === 'conversation_custom_attribute' ||
                  rule.condition.type === 'contact_custom_attribute_equals'
                "
              >
                <div class="grid grid-cols-1 gap-2">
                  <select
                    v-model="rule.condition.key"
                    class="w-full px-3 py-2 border border-n-weak rounded-xl text-sm bg-n-solid-1"
                  >
                    <option value="">
                      {{ t('KANBAN.STAGE_AUTOMATIONS.ATTR_NAME_PLACEHOLDER') }}
                    </option>
                    <option
                      v-for="attr in rule.condition.type ===
                      'conversation_custom_attribute'
                        ? conversationCustomAttributeOptions
                        : contactCustomAttributeOptions"
                      :key="attr.key"
                      :value="attr.key"
                    >
                      {{ attr.name }}
                    </option>
                  </select>
                  <select
                    v-model="rule.condition.operator"
                    class="w-full px-3 py-2 border border-n-weak rounded-xl text-sm bg-n-solid-1"
                  >
                    <option
                      v-for="op in stringOperators"
                      :key="op.id"
                      :value="op.id"
                    >
                      {{ opLabel(op.labelKey) }}
                    </option>
                  </select>
                  <input
                    v-model="rule.condition.value"
                    type="text"
                    class="w-full px-3 py-2 border border-n-weak rounded-xl text-sm bg-n-solid-1"
                    :placeholder="
                      t('KANBAN.STAGE_AUTOMATIONS.ATTR_VALUE_PLACEHOLDER')
                    "
                  />
                </div>
              </template>
            </div>

            <div>
              <label class="block text-xs font-medium text-n-slate-11 mb-1">
                {{ t('KANBAN.STAGE_AUTOMATIONS.ACTION') }}
              </label>
              <select
                v-model="rule.action"
                class="w-full px-3 py-2 border border-n-weak rounded-xl text-sm bg-n-solid-1"
                @change="onStageAutomationActionChange(rule)"
              >
                <option value="assign_agent">
                  {{ t('KANBAN.STAGE_AUTOMATIONS.ACTION_ASSIGN_AGENT') }}
                </option>
                <option value="clear_assigned_agents">
                  {{ t('KANBAN.STAGE_AUTOMATIONS.ACTION_CLEAR_AGENTS') }}
                </option>
                <option value="create_note">
                  {{ t('KANBAN.STAGE_AUTOMATIONS.ACTION_NOTE') }}
                </option>
                <option value="move_to_stage">
                  {{ t('KANBAN.STAGE_AUTOMATIONS.ACTION_MOVE_STAGE') }}
                </option>
                <option value="move_to_funnel">
                  {{ t('KANBAN.STAGE_AUTOMATIONS.ACTION_MOVE_FUNNEL') }}
                </option>
                <option value="set_item_status">
                  {{ t('KANBAN.STAGE_AUTOMATIONS.ACTION_SET_STATUS') }}
                </option>
                <option value="set_item_priority">
                  {{ t('KANBAN.STAGE_AUTOMATIONS.ACTION_SET_PRIORITY') }}
                </option>
                <option value="set_item_value">
                  {{ t('KANBAN.STAGE_AUTOMATIONS.ACTION_SET_VALUE') }}
                </option>
                <option value="set_item_custom_attribute">
                  {{ t('KANBAN.STAGE_AUTOMATIONS.ACTION_SET_CUSTOM_ATTR') }}
                </option>
                <option value="complete_required_checklist">
                  {{ t('KANBAN.STAGE_AUTOMATIONS.ACTION_COMPLETE_CHECKLIST') }}
                </option>
                <option value="webhook">
                  {{ t('KANBAN.STAGE_AUTOMATIONS.ACTION_WEBHOOK') }}
                </option>
                <option value="create_task">Criar tarefa</option>
              </select>
            </div>

            <div v-if="rule.action === 'assign_agent'">
              <label class="block text-xs font-medium text-n-slate-11 mb-1">
                {{ t('KANBAN.STAGE_AUTOMATIONS.AGENT') }}
              </label>
              <select
                v-model.number="rule.action_config.agent_id"
                class="w-full px-3 py-2 border border-n-weak rounded-xl text-sm bg-n-solid-1"
              >
                <option value="">
                  {{ t('KANBAN.STAGE_AUTOMATIONS.SELECT_AGENT') }}
                </option>
                <option
                  v-for="agent in agentsList"
                  :key="agent.id"
                  :value="agent.id"
                >
                  {{ agent.name }}
                </option>
              </select>
            </div>

            <div v-if="rule.action === 'create_task'" class="space-y-2">
              <label class="block text-xs font-medium text-n-slate-11">Título da tarefa *</label>
              <input
                v-model="rule.action_config.title"
                type="text"
                class="w-full px-3 py-2 border border-n-weak rounded-xl text-sm bg-n-solid-1"
                placeholder="Ex: Ligar pro lead"
              />
              <label class="block text-xs font-medium text-n-slate-11">Descrição</label>
              <textarea
                v-model="rule.action_config.description"
                rows="2"
                class="w-full px-3 py-2 border border-n-weak rounded-xl text-sm bg-n-solid-1"
                placeholder="Opcional"
              />
              <div class="grid grid-cols-2 gap-2">
                <div>
                  <label class="block text-xs font-medium text-n-slate-11">Prioridade</label>
                  <select
                    v-model="rule.action_config.priority"
                    class="w-full px-3 py-2 border border-n-weak rounded-xl text-sm bg-n-solid-1"
                  >
                    <option value="none">Sem</option>
                    <option value="low">Baixa</option>
                    <option value="medium">Média</option>
                    <option value="high">Alta</option>
                    <option value="urgent">Urgente</option>
                  </select>
                </div>
                <div>
                  <label class="block text-xs font-medium text-n-slate-11">Vencimento (dias após)</label>
                  <input
                    v-model.number="rule.action_config.due_offset_days"
                    type="number"
                    min="0"
                    class="w-full px-3 py-2 border border-n-weak rounded-xl text-sm bg-n-solid-1"
                    placeholder="0 = sem vencimento"
                  />
                </div>
              </div>
              <label class="block text-xs font-medium text-n-slate-11">Atribuir a</label>
              <select
                v-model.number="rule.action_config.assigned_to_user_id"
                class="w-full px-3 py-2 border border-n-weak rounded-xl text-sm bg-n-solid-1"
              >
                <option value="">— Sem atribuição —</option>
                <option
                  v-for="agent in agentsList"
                  :key="agent.id"
                  :value="agent.id"
                >
                  {{ agent.name }}
                </option>
              </select>
            </div>

            <div v-if="rule.action === 'move_to_stage'">
              <label class="block text-xs font-medium text-n-slate-11 mb-1">
                {{ t('KANBAN.STAGE_AUTOMATIONS.TARGET_STAGE') }}
              </label>
              <select
                v-model="rule.action_config.stage"
                class="w-full px-3 py-2 border border-n-weak rounded-xl text-sm bg-n-solid-1"
              >
                <option value="">
                  {{ t('KANBAN.STAGE_AUTOMATIONS.SELECT_STAGE') }}
                </option>
                <option v-for="st in otherStages" :key="st.id" :value="st.id">
                  {{ st.name }}
                </option>
              </select>
            </div>

            <div
              v-if="rule.action === 'move_to_funnel'"
              class="grid grid-cols-1 sm:grid-cols-2 gap-2"
            >
              <div>
                <label class="block text-xs font-medium text-n-slate-11 mb-1">
                  {{ t('KANBAN.STAGE_AUTOMATIONS.TARGET_FUNNEL') }}
                </label>
                <select
                  v-model="rule.action_config.funnel_id"
                  class="w-full px-3 py-2 border border-n-weak rounded-xl text-sm bg-n-solid-1"
                  @change="onMoveToFunnelTargetChange(rule)"
                >
                  <option value="">
                    {{ t('KANBAN.STAGE_AUTOMATIONS.SELECT_FUNNEL') }}
                  </option>
                  <option
                    v-for="funnel in funnelOptions"
                    :key="funnel.id"
                    :value="funnel.id"
                  >
                    {{ funnel.name
                    }}{{
                      funnel.isCurrent
                        ? ` (${t('KANBAN.STAGE_AUTOMATIONS.CURRENT_FUNNEL')})`
                        : ''
                    }}
                  </option>
                </select>
              </div>
              <div>
                <label class="block text-xs font-medium text-n-slate-11 mb-1">
                  {{ t('KANBAN.STAGE_AUTOMATIONS.TARGET_STAGE') }}
                </label>
                <select
                  v-model="rule.action_config.stage"
                  class="w-full px-3 py-2 border border-n-weak rounded-xl text-sm bg-n-solid-1"
                  :disabled="!rule.action_config.funnel_id"
                >
                  <option value="">
                    {{ t('KANBAN.STAGE_AUTOMATIONS.SELECT_STAGE') }}
                  </option>
                  <option
                    v-for="st in targetStagesByFunnel[
                      rule.action_config.funnel_id
                    ] || []"
                    :key="st.id"
                    :value="st.id"
                  >
                    {{ st.name }}
                  </option>
                </select>
              </div>
            </div>

            <div v-if="rule.action === 'create_note'">
              <label class="block text-xs font-medium text-n-slate-11 mb-1">
                {{ t('KANBAN.STAGE_AUTOMATIONS.NOTE_TEXT') }}
              </label>
              <textarea
                v-model="rule.action_config.note_text"
                rows="2"
                class="w-full px-3 py-2 border border-n-weak rounded-xl text-sm bg-n-solid-1"
              />
            </div>

            <div v-if="rule.action === 'set_item_status'">
              <label class="block text-xs font-medium text-n-slate-11 mb-1">
                {{ t('KANBAN.STAGE_AUTOMATIONS.DEAL_STATUS') }}
              </label>
              <select
                v-model="rule.action_config.status"
                class="w-full px-3 py-2 border border-n-weak rounded-xl text-sm bg-n-solid-1"
              >
                <option
                  v-for="opt in dealStatusOptions"
                  :key="opt.id"
                  :value="opt.id"
                >
                  {{ t(`KANBAN.STAGE_AUTOMATIONS.${opt.labelKey}`) }}
                </option>
              </select>
            </div>

            <div v-if="rule.action === 'set_item_priority'">
              <label class="block text-xs font-medium text-n-slate-11 mb-1">
                {{ t('KANBAN.STAGE_AUTOMATIONS.PRIORITY') }}
              </label>
              <select
                v-model="rule.action_config.priority"
                class="w-full px-3 py-2 border border-n-weak rounded-xl text-sm bg-n-solid-1"
              >
                <option
                  v-for="opt in priorityOptions"
                  :key="opt.id"
                  :value="opt.id"
                >
                  {{ t(`KANBAN.STAGE_AUTOMATIONS.${opt.labelKey}`) }}
                </option>
              </select>
            </div>

            <div v-if="rule.action === 'set_item_value'">
              <label class="block text-xs font-medium text-n-slate-11 mb-1">
                {{ t('KANBAN.STAGE_AUTOMATIONS.VALUE_AMOUNT') }}
              </label>
              <input
                v-model="rule.action_config.value"
                type="number"
                step="any"
                class="w-full px-3 py-2 border border-n-weak rounded-xl text-sm bg-n-solid-1"
              />
            </div>

            <div v-if="rule.action === 'set_item_custom_attribute'">
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <input
                  v-model="rule.action_config.key"
                  type="text"
                  class="w-full px-3 py-2 border border-n-weak rounded-xl text-sm bg-n-solid-1"
                  :placeholder="
                    t('KANBAN.STAGE_AUTOMATIONS.ATTR_NAME_PLACEHOLDER')
                  "
                />
                <input
                  v-model="rule.action_config.value"
                  type="text"
                  class="w-full px-3 py-2 border border-n-weak rounded-xl text-sm bg-n-solid-1"
                  :placeholder="
                    t('KANBAN.STAGE_AUTOMATIONS.ATTR_VALUE_PLACEHOLDER')
                  "
                />
              </div>
            </div>

            <div v-if="rule.action === 'webhook'">
              <label class="block text-xs font-medium text-n-slate-11 mb-1">
                {{ t('KANBAN.STAGE_AUTOMATIONS.WEBHOOK_URL') }}
              </label>
              <input
                v-model="rule.action_config.url"
                type="url"
                class="w-full px-3 py-2 border border-n-weak rounded-xl text-sm bg-n-solid-1"
                :placeholder="
                  t('KANBAN.STAGE_AUTOMATIONS.WEBHOOK_URL_PLACEHOLDER')
                "
              />
              <p class="text-xs text-n-slate-11 mt-1">
                {{ t('KANBAN.STAGE_AUTOMATIONS.WEBHOOK_HELP') }}
              </p>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<style scoped>
.custom-colored-button {
  background-color: var(--button-bg-color) !important;
  color: var(--button-text-color) !important;
  border: 1px solid var(--button-bg-color) !important;
}

.custom-colored-button:hover:not(:disabled) {
  background-color: var(--button-hover-bg-color) !important;
  border-color: var(--button-hover-bg-color) !important;
  color: var(--button-hover-text-color, var(--button-text-color)) !important;
}

.custom-colored-button:focus-visible {
  background-color: var(--button-hover-bg-color) !important;
  border-color: var(--button-hover-bg-color) !important;
  color: var(--button-hover-text-color, var(--button-text-color)) !important;
}

.stage-back-button:hover {
  background-color: var(--stage-back-btn-hover-bg, rgba(148, 163, 184, 0.25));
}

.empty-rules-placeholder__icon-wrapper {
  width: 4.25rem;
  height: 4.25rem;
  display: grid;
  place-items: center;
  border-radius: 9999px;
  background: radial-gradient(
    circle at 30% 30%,
    rgba(59, 130, 246, 0.2),
    rgba(59, 130, 246, 0.08) 60%,
    transparent
  );
}

.empty-rules-placeholder__icon {
  width: 2.8rem;
  height: 2.8rem;
}

.empty-rules-placeholder__shape {
  fill: rgba(59, 130, 246, 0.15);
  stroke: rgba(59, 130, 246, 0.45);
  stroke-width: 1.5;
}

.empty-rules-placeholder__line {
  stroke: rgba(37, 99, 235, 0.85);
  stroke-width: 2;
  stroke-linecap: round;
}

.empty-rules-placeholder__plus-bg {
  fill: rgba(37, 99, 235, 0.22);
}

.empty-rules-placeholder__plus {
  stroke: rgba(29, 78, 216, 0.95);
  stroke-width: 2;
  stroke-linecap: round;
}

.dark .empty-rules-placeholder__shape {
  fill: rgba(148, 163, 184, 0.15);
  stroke: rgba(148, 163, 184, 0.45);
}

.dark .empty-rules-placeholder__line {
  stroke: rgba(191, 219, 254, 0.9);
}

.dark .empty-rules-placeholder__plus-bg {
  fill: rgba(125, 211, 252, 0.28);
}

.dark .empty-rules-placeholder__plus {
  stroke: rgba(224, 242, 254, 0.95);
}
</style>
